/**
 * Tracks whether the page is being left (refresh, navigation away).
 *
 * The browser cancels the page's in-flight requests then, and they reject like
 * network errors. During the auth check that would read as a failed login: the
 * "loginFailed" notification lands in sessionStorage and shows on the next
 * page, where the user is signed in fine. Callers use this to tell the two
 * apart.
 *
 * `beforeunload` fires when the navigation starts, before the requests are
 * cancelled, but a page's "unsaved changes" prompt can still cancel it, so
 * that flag clears itself again. `pagehide` means the page is really going.
 */
const BEFOREUNLOAD_GRACE_MS = 2000;

let isUnloading = false;
let resetTimer: ReturnType<typeof setTimeout> | undefined;

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    isUnloading = true;
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => {
      isUnloading = false;
    }, BEFOREUNLOAD_GRACE_MS);
  });
  window.addEventListener("pagehide", () => {
    clearTimeout(resetTimer);
    isUnloading = true;
  });
  // Restored from the back/forward cache: the page is alive again.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) isUnloading = false;
  });
}

export const isPageUnloading = (): boolean => isUnloading;
